import { createElement, useState, type ReactNode } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { BlurView } from "expo-blur";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type GlassButtonShape = "pill" | "rounded";
export type GlassButtonSize = "compact";
export type GlassButtonVariant = "default" | "shimmer" | "frosted";

export type GlassButtonProps = Omit<PressableProps, "children" | "style"> & {
  label: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  variant?: GlassButtonVariant;
  size?: GlassButtonSize;
  shape?: GlassButtonShape;
  radius?: number;
  iconOnly?: boolean;
  leading?: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  fill?: boolean;
  width?: number;
  blur?: number | false;
  backgroundColor?: string;
  backgroundGradient?: readonly [string, string];
  borderColor?: string;
  textColor?: string;
  pressedOpacity?: number;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

const SHAPE_RADIUS: Record<GlassButtonShape, number> = {
  pill: 999,
  rounded: 8,
};

const COMPACT_TEXT_COLOR = "rgba(255,247,239,0.92)";
const SHIMMER_BACKGROUND_GRADIENT = [
  "rgba(255, 255, 255, 0.12)",
  "rgba(255, 255, 255, 0.06)",
] as const;

if (Platform.OS === "web" && typeof document !== "undefined") {
  const id = "moon-glass-button-styles";
  if (!document.getElementById(id)) {
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      .glass-button-shimmer {
        transition: filter 0.2s ease, box-shadow 0.2s ease;
      }
      .glass-button-shimmer:hover {
        filter: brightness(1.25);
        box-shadow: 0 0 24px rgba(255, 255, 255, 0.12);
      }
    `;
    document.head.appendChild(style);
  }
}

function ShimmerBorder({ radius }: { radius: number }) {
  if (Platform.OS !== "web") return null;

  return createElement(
    "svg",
    {
      style: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
      },
    },
    createElement(
      "defs",
      null,
      createElement(
        "linearGradient",
        {
          id: "moon-glass-button-shimmer",
          x1: "0%",
          y1: "0%",
          x2: "100%",
          y2: "100%",
        },
        createElement("stop", {
          offset: "0%",
          stopColor: "rgba(255, 255, 255, 0)",
        }),
        createElement("stop", {
          offset: "40%",
          stopColor: "rgba(255, 255, 255, 0)",
        }),
        createElement("stop", {
          offset: "50%",
          stopColor: "rgba(255, 255, 255, 0.8)",
        }),
        createElement("stop", {
          offset: "60%",
          stopColor: "rgba(255, 255, 255, 0)",
        }),
        createElement("stop", {
          offset: "100%",
          stopColor: "rgba(255, 255, 255, 0)",
        }),
        createElement("animate", {
          attributeName: "x1",
          values: "-100%;100%",
          dur: "2.5s",
          repeatCount: "indefinite",
        }),
        createElement("animate", {
          attributeName: "x2",
          values: "0%;200%",
          dur: "2.5s",
          repeatCount: "indefinite",
        }),
      ),
    ),
    createElement("rect", {
      x: "0",
      y: "0",
      width: "100%",
      height: "100%",
      rx: radius,
      ry: radius,
      fill: "none",
      stroke: "url(#moon-glass-button-shimmer)",
      strokeWidth: "1.5",
    }),
  );
}

export function GlassButton({
  label,
  variant = "default",
  size = "compact",
  shape,
  radius,
  iconOnly = false,
  leading,
  icon,
  trailing,
  fill = false,
  width,
  blur = 8,
  backgroundColor = "rgba(255,255,255,0.1)",
  backgroundGradient,
  borderColor = "rgba(255,255,255,0.16)",
  textColor = COMPACT_TEXT_COLOR,
  pressedOpacity = 0.74,
  loading = false,
  disabled,
  action,
  accessibilityLabel,
  style,
  textStyle,
  onHoverIn,
  onHoverOut,
  onPress,
  ...pressableProps
}: GlassButtonProps) {
  const capturePress = usePressCapture("GlassButton");
  const isShimmer = variant === "shimmer";
  const isFrosted = variant === "frosted";
  const resolvedBackgroundGradient =
    backgroundGradient ?? (isShimmer ? SHIMMER_BACKGROUND_GRADIENT : undefined);
  const resolvedShape = shape ?? (isShimmer ? "rounded" : "pill");
  const resolvedRadius =
    radius ??
    (isShimmer
      ? resolvedShape === "pill"
        ? SHAPE_RADIUS.pill
        : 14
      : SHAPE_RADIUS[resolvedShape]);
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || loading;
  const content = (
    <>
      {leading}
      {icon}
      {iconOnly || loading ? null : (
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={[
            {
              fontSize: isShimmer ? 15 : isFrosted ? 14 : 13,
              lineHeight: isShimmer ? 20 : isFrosted ? undefined : 16,
              color: isShimmer
                ? "#FFFFFF"
                : isFrosted
                  ? "rgba(255, 255, 255, 0.94)"
                  : textColor,
              fontWeight: isShimmer || isFrosted ? "500" : "800",
              letterSpacing: isShimmer ? 0.3 : undefined,
              textShadowColor: isFrosted ? "rgba(25, 7, 1, 0.16)" : undefined,
              textShadowOffset: isFrosted ? { width: 0, height: 1 } : undefined,
              textShadowRadius: isFrosted ? 2 : undefined,
            },
            textStyle,
          ]}
        >
          {label}
        </Text>
      )}
      {loading ? <ActivityIndicator color="#FFFFFF" /> : trailing}
    </>
  );

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      disabled={isDisabled}
      onHoverIn={(event) => {
        setHovered(true);
        onHoverIn?.(event);
      }}
      onHoverOut={(event) => {
        setHovered(false);
        onHoverOut?.(event);
      }}
      style={({ pressed }) => [
        getSizeStyle({
          variant,
          size,
          iconOnly,
          fill,
          width,
          radius: resolvedRadius,
          backgroundColor,
          hasBackgroundGradient: resolvedBackgroundGradient !== undefined,
          borderColor,
          opacity: isDisabled
            ? isShimmer
              ? 0.6
              : 0.45
            : pressed
              ? pressedOpacity
              : 1,
          blur,
          pressed,
          hovered,
          isDisabled,
        }),
        style,
      ]}
      {...(isShimmer && Platform.OS === "web"
        ? { className: "glass-button-shimmer" }
        : {})}
      {...pressableProps}
      onPress={(event) => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress?.(event);
      }}
    >
      {isFrosted ? (
        <View style={styles.frostedInner}>
          {Platform.OS !== "web" && (
            <BlurView
              pointerEvents="none"
              intensity={28}
              tint="default"
              experimentalBlurMethod="dimezisBlurView"
              style={StyleSheet.absoluteFill}
            />
          )}
          <LinearGradient
            pointerEvents="none"
            colors={[
              "rgba(255, 255, 255, 0.045)",
              "rgba(255, 255, 255, 0.003)",
              "rgba(255, 255, 255, 0.01)",
            ]}
            locations={[0, 0.48, 1]}
            start={{ x: 0.04, y: 0 }}
            end={{ x: 0.96, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.frostedContent}>{content}</View>
        </View>
      ) : isShimmer ? (
        <View style={styles.shimmerInner}>
          {!loading && <ShimmerBorder radius={resolvedRadius} />}
          <LinearGradient
            colors={resolvedBackgroundGradient ?? SHIMMER_BACKGROUND_GRADIENT}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={[styles.shimmerGradient, { borderRadius: resolvedRadius }]}
          >
            {content}
          </LinearGradient>
        </View>
      ) : (
        <>
          {resolvedBackgroundGradient && (
            <LinearGradient
              pointerEvents="none"
              colors={resolvedBackgroundGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={[
                StyleSheet.absoluteFill,
                {
                  borderRadius: resolvedRadius,
                  borderWidth: 1,
                  borderColor: "rgba(255, 255, 255, 0.1)",
                },
              ]}
            />
          )}
          {content}
        </>
      )}
    </Pressable>
  );
}

function getSizeStyle({
  variant,
  size,
  iconOnly,
  fill,
  width,
  radius,
  backgroundColor,
  hasBackgroundGradient,
  borderColor,
  opacity,
  blur,
  pressed,
  hovered,
  isDisabled,
}: {
  variant: GlassButtonVariant;
  size: GlassButtonSize;
  iconOnly: boolean;
  fill: boolean;
  width?: number;
  radius: number;
  backgroundColor: string;
  hasBackgroundGradient: boolean;
  borderColor: string;
  opacity: number;
  blur: number | false;
  pressed: boolean;
  hovered: boolean;
  isDisabled: boolean;
}): ViewStyle {
  if (variant === "frosted") {
    const isInteractive = hovered || pressed;
    return {
      position: "relative",
      height: 54,
      borderWidth: 1,
      borderRadius: radius,
      borderColor: isInteractive
        ? "rgba(255, 255, 255, 0.36)"
        : "rgba(255, 255, 255, 0.22)",
      borderTopColor: isInteractive
        ? "rgba(255, 255, 255, 0.56)"
        : "rgba(255, 255, 255, 0.38)",
      borderBottomColor: "rgba(255, 255, 255, 0.08)",
      backgroundColor: pressed
        ? "rgba(255, 255, 255, 0.06)"
        : hovered
          ? "rgba(255, 255, 255, 0.035)"
          : "rgba(255, 255, 255, 0.006)",
      overflow: "hidden",
      opacity: isDisabled ? 0.55 : 1,
      transform: [{ scale: pressed && !isDisabled ? 0.985 : 1 }],
      shadowColor: "#160803",
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.16,
      shadowRadius: 14,
      elevation: 1,
      ...(Platform.OS === "web"
        ? ({
            backdropFilter: "blur(10px) saturate(0.92) brightness(1.04)",
            WebkitBackdropFilter: "blur(10px) saturate(0.92) brightness(1.04)",
            transition:
              "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
            cursor: isDisabled ? "default" : "pointer",
          } as ViewStyle)
        : {}),
    };
  }

  if (variant === "shimmer") {
    return {
      flexGrow: fill ? 1 : 0,
      flexBasis: fill ? 0 : undefined,
      width,
      flexShrink: 0,
      borderRadius: radius,
      overflow: "hidden",
      backgroundColor: "transparent",
      borderWidth: 0,
      padding: 0,
      opacity,
      transform: [{ scale: pressed ? 0.98 : 1 }],
      shadowColor: "rgba(255, 255, 255, 0.5)",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: pressed ? 0.2 : 0.1,
      shadowRadius: pressed ? 24 : 16,
    };
  }

  switch (size) {
    case "compact":
      return {
        flexGrow: fill ? 1 : 0,
        flexBasis: fill ? 0 : undefined,
        width,
        flexShrink: 0,
        minHeight: 38,
        borderRadius: radius,
        backgroundColor: hasBackgroundGradient
          ? "transparent"
          : backgroundColor,
        borderWidth: hasBackgroundGradient ? 0 : 1,
        borderColor: hasBackgroundGradient ? "transparent" : borderColor,
        paddingHorizontal: iconOnly ? 0 : 8,
        paddingVertical: 7,
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
        gap: iconOnly ? 4 : 5,
        opacity,
        overflow: hasBackgroundGradient ? "hidden" : undefined,
        ...(Platform.OS === "web" && blur !== false
          ? ({
              backdropFilter: `blur(${blur}px)`,
              WebkitBackdropFilter: `blur(${blur}px)`,
            } as Record<string, string>)
          : null),
      };
  }
}

const styles = StyleSheet.create({
  shimmerInner: {
    position: "relative",
    width: "100%",
  },
  shimmerGradient: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.1)",
  },
  frostedInner: {
    position: "relative",
    flex: 1,
    width: "100%",
  },
  frostedContent: {
    zIndex: 1,
    flex: 1,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
  },
});

export default GlassButton;
